import Vue from 'vue'
import { getMarketOverview } from '@/api'
import {
  buildQuoteCacheEntry,
  normalizeQuoteSymbol,
  toMarketOverviewCode
} from '@/utils/quoteCache'

const symbolInFlightPromises = new Map()
const pendingSymbols = new Set()
const pendingSymbolSources = new Map()
let pendingBatchPromise = null

function createDeferred() {
  let resolve
  let reject
  const promise = new Promise((res, rej) => {
    resolve = res
    reject = rej
  })
  return { promise, resolve, reject }
}

function parsePositiveNumber(...values) {
  for (const value of values) {
    const numberValue = Number(value)
    if (Number.isFinite(numberValue) && numberValue > 0) {
      return numberValue
    }
  }
  return 0
}

function parseFiniteNumber(...values) {
  for (const value of values) {
    const numberValue = Number(value)
    if (Number.isFinite(numberValue)) {
      return numberValue
    }
  }
  return NaN
}

function buildQuoteEntryFromRow(row = {}, source = 'positions') {
  const symbol = normalizeQuoteSymbol(
    row?.symbol || row?.code || row?.stock_code || row?.stockCode || row?.ticker || ''
  )
  if (!symbol) return null

  const rawQuote = row?.rawQuote || row?.quote || null
  const extra = {
    symbol,
    code: toMarketOverviewCode(symbol),
    quotePrice: parsePositiveNumber(row?.quotePrice, row?.quote_price),
    quoteAvgPrice: parsePositiveNumber(row?.quoteAvgPrice, row?.quote_avg_price, row?.avgPrice, row?.avg_price),
    quoteAmplitude: parseFiniteNumber(row?.quoteAmplitude, row?.quote_amplitude, row?.amplitude),
    quoteChangeRate: parseFiniteNumber(row?.quoteChangeRate, row?.quote_change_rate, row?.changeRate, row?.change_rate),
    updatedAt: Date.now(),
    source
  }

  if (rawQuote && typeof rawQuote === 'object') {
    return buildQuoteCacheEntry(rawQuote, extra)
  }

  if (!(extra.quotePrice > 0) && !(extra.quoteAvgPrice > 0) && !Number.isFinite(extra.quoteChangeRate)) {
    return null
  }

  return {
    symbol,
    code: extra.code,
    rawQuote: null,
    quotePrice: extra.quotePrice,
    quoteAvgPrice: extra.quoteAvgPrice,
    quoteAmplitude: Number.isFinite(extra.quoteAmplitude) ? extra.quoteAmplitude : 0,
    quoteChangeRate: Number.isFinite(extra.quoteChangeRate) ? extra.quoteChangeRate : 0,
    updatedAt: extra.updatedAt,
    source: extra.source
  }
}

function isFresh(entry, ttlMs) {
  return !!entry && Number(entry.updatedAt) > 0 && (Date.now() - Number(entry.updatedAt)) < ttlMs
}

function hasCompleteMarketOverview(entry) {
  return !!(entry && entry.rawQuote && typeof entry.rawQuote === 'object' && Object.keys(entry.rawQuote).length > 0)
}

function flushPendingBatch(commit) {
  const symbolsToFetch = Array.from(pendingSymbols).sort()
  pendingSymbols.clear()
  pendingBatchPromise = null

  console.log('[quotes] flushPendingBatch:start', {
    symbolsToFetch
  })

  if (!symbolsToFetch.length) {
    console.log('[quotes] flushPendingBatch:skip-empty')
    return Promise.resolve([])
  }

  const codes = symbolsToFetch.map(toMarketOverviewCode).filter(Boolean).sort()
  if (!codes.length) {
    console.log('[quotes] flushPendingBatch:skip-no-codes', { symbolsToFetch })
    symbolsToFetch.forEach((symbol) => {
      const deferred = symbolInFlightPromises.get(symbol)
      if (deferred) {
        deferred.resolve(null)
        symbolInFlightPromises.delete(symbol)
      }
      pendingSymbolSources.delete(symbol)
    })
    return Promise.resolve([])
  }

  console.log('[quotes] getMarketOverview:request', {
    codes
  })

  return getMarketOverview(codes.join(','))
    .then((res) => {
      const quoteList = res?.data?.data || []
      console.log('[quotes] getMarketOverview:response', {
        requestCount: codes.length,
        responseCount: quoteList.length,
        codes
      })
      const entryMap = {}
      const entries = quoteList
        .map((quote) => {
          const symbol = normalizeQuoteSymbol(quote?.code || quote?.symbol || '')
          const source = pendingSymbolSources.get(symbol) || 'market-overview'
          const entry = buildQuoteCacheEntry(quote, { source })
          if (entry?.symbol) {
            entryMap[entry.symbol] = entry
          }
          return entry
        })
        .filter(Boolean)

      if (entries.length) {
        commit('MERGE_QUOTES', entries)
      }

      console.log('[quotes] MERGE_QUOTES:from-market-overview', {
        entrySymbols: entries.map(item => item.symbol)
      })

      symbolsToFetch.forEach((symbol) => {
        const deferred = symbolInFlightPromises.get(symbol)
        if (deferred) {
          deferred.resolve(entryMap[symbol] || null)
          symbolInFlightPromises.delete(symbol)
        }
        pendingSymbolSources.delete(symbol)
      })

      return entries
    })
    .catch((error) => {
      console.error('[quotes] getMarketOverview:error', {
        codes,
        message: error?.message || error
      })
      symbolsToFetch.forEach((symbol) => {
        const deferred = symbolInFlightPromises.get(symbol)
        if (deferred) {
          deferred.reject(error)
          symbolInFlightPromises.delete(symbol)
        }
        pendingSymbolSources.delete(symbol)
      })
      throw error
    })
}

function ensurePendingBatchScheduled(commit) {
  if (!pendingBatchPromise) {
    pendingBatchPromise = Promise.resolve().then(() => flushPendingBatch(commit))
  }
  return pendingBatchPromise
}

export default {
  namespaced: true,
  state: () => ({
    quoteMap: {}
  }),
  getters: {
    getQuote: (state) => (symbol) => state.quoteMap[normalizeQuoteSymbol(symbol)] || null,
    getQuotePrice: (state, getters) => (symbol) => Number(getters.getQuote(symbol)?.quotePrice || 0),
    getQuoteMapBySymbols: (state) => (symbols = []) => {
      const result = {}
      symbols.forEach((symbol) => {
        const normalizedSymbol = normalizeQuoteSymbol(symbol)
        if (normalizedSymbol && state.quoteMap[normalizedSymbol]) {
          result[normalizedSymbol] = state.quoteMap[normalizedSymbol]
        }
      })
      return result
    }
  },
  mutations: {
    MERGE_QUOTES(state, entries = []) {
      entries.forEach((entry) => {
        if (!entry || !entry.symbol) return
        const symbol = normalizeQuoteSymbol(entry.symbol)
        if (!symbol) return
        const previous = state.quoteMap[symbol] || {}
        const mergedEntry = {
          ...previous,
          ...entry,
          symbol
        }
        if (!hasCompleteMarketOverview(entry) && hasCompleteMarketOverview(previous)) {
          mergedEntry.rawQuote = previous.rawQuote
          if (!(Number(entry.quotePrice) > 0) && Number(previous.quotePrice) > 0) mergedEntry.quotePrice = previous.quotePrice
          if (!(Number(entry.quoteAvgPrice) > 0) && Number(previous.quoteAvgPrice) > 0) mergedEntry.quoteAvgPrice = previous.quoteAvgPrice
          if (!(Number(entry.quoteAmplitude) > 0) && Number(previous.quoteAmplitude) > 0) mergedEntry.quoteAmplitude = previous.quoteAmplitude
          if (!Number.isFinite(Number(entry.quoteChangeRate)) && Number.isFinite(Number(previous.quoteChangeRate))) {
            mergedEntry.quoteChangeRate = previous.quoteChangeRate
          }
        }
        Vue.set(state.quoteMap, symbol, mergedEntry)
      })
    }
  },
  actions: {
    mergeQuoteRows({ commit }, { rows = [], source = 'positions' } = {}) {
      const entries = rows
        .map((row) => buildQuoteEntryFromRow(row, source))
        .filter(Boolean)
      console.log('[quotes] mergeQuoteRows', {
        source,
        rowCount: rows.length,
        entryCount: entries.length,
        sampleSymbols: entries.slice(0, 10).map(item => item.symbol)
      })
      if (entries.length) {
        commit('MERGE_QUOTES', entries)
      }
      return entries
    },
    mergeQuoteList({ commit }, { quoteList = [], source = 'market-overview' } = {}) {
      const entries = quoteList
        .map((quote) => buildQuoteCacheEntry(quote, { source }))
        .filter(Boolean)
      if (entries.length) {
        commit('MERGE_QUOTES', entries)
      }
      return entries
    },
    async fetchQuotesBySymbols({ state, commit }, { symbols = [], ttlMs = 6000, force = false, source = 'market-overview' } = {}) {
      const normalizedSymbols = Array.from(new Set((symbols || []).map(normalizeQuoteSymbol).filter(Boolean))).sort()
      if (!normalizedSymbols.length) return []

      console.log('[quotes] fetchQuotesBySymbols:input', {
        symbols: normalizedSymbols,
        ttlMs,
        force,
        source
      })

      const waitPromises = []
      const cacheHitSymbols = []
      const staleSymbols = []
      normalizedSymbols.forEach((symbol) => {
        const cachedEntry = state.quoteMap[symbol]
        if (!force && isFresh(cachedEntry, ttlMs) && hasCompleteMarketOverview(cachedEntry)) {
          cacheHitSymbols.push(symbol)
          return
        }
        staleSymbols.push({
          symbol,
          hasCache: !!cachedEntry,
          hasRawQuote: hasCompleteMarketOverview(cachedEntry),
          updatedAt: cachedEntry?.updatedAt || null
        })

        let deferred = symbolInFlightPromises.get(symbol)
        if (!deferred) {
          deferred = createDeferred()
          symbolInFlightPromises.set(symbol, deferred)
          pendingSymbols.add(symbol)
        }
        pendingSymbolSources.set(symbol, source)
        waitPromises.push(deferred.promise)
      })

      console.log('[quotes] fetchQuotesBySymbols:decision', {
        cacheHitSymbols,
        staleSymbols
      })

      if (waitPromises.length) {
        await ensurePendingBatchScheduled(commit)
        await Promise.allSettled(waitPromises)
      }

      console.log('[quotes] fetchQuotesBySymbols:done', {
        symbols: normalizedSymbols,
        resultSymbols: normalizedSymbols.filter(symbol => !!state.quoteMap[symbol])
      })
      return normalizedSymbols.map((symbol) => state.quoteMap[symbol]).filter(Boolean)
    }
  }
}
